import { Injectable, signal } from '@angular/core';
import { ValueChangeEvent } from '@angular/forms';
import { assert } from 'src/app/utils/assert.util';

@Injectable({
  providedIn: 'root'
})
export class IndexedDBProviderService {

  constructor() { }

  status = signal<'closed' | 'open'>('closed');
  private db?: IDBDatabase;

  async open(dbName: string, version: number, upgrade?: (db: IDBDatabase) => void) {
    if (this.db && this.db.name === dbName && this.db.version === version) {
      console.log('The db is already open');
      return;
    } else if (this.db) {
      this.close();
    }

    try {
      this.db = await new Promise((res, rej) => {
        const dbRequest = indexedDB.open(dbName, version);
        dbRequest.onupgradeneeded = () => upgrade?.call(this, dbRequest.result);
        dbRequest.onsuccess = () => { res(dbRequest.result); }
        dbRequest.onerror = () => rej();
        dbRequest.onblocked = () => rej();
      });

      this.status.set('open');
      this.db.onclose = () => this.status.set('closed');
    } catch(e) {
      console.error('Failed to open db');
      this.status.set('closed');
    }
  }

  close() {
    this.db?.close();
    this.status.set('closed');
  }

  async write<T>(store: string, key: string, data: T) {
    if (this.db?.objectStoreNames)

    return new Promise<void>((res, rej) => {
      try {
        const transaction = this.db?.transaction(store, 'readwrite');
        assert(transaction, 'Failed to create db transaction');
        transaction?.objectStore(store).put(data, key);
        transaction?.commit();
        transaction.oncomplete = () => res();
        transaction.onerror = () => rej();
        transaction.onabort = () => rej();
      } catch(e) {
        rej();
      }
    });
  }

  async loadSingle<T>(store: string, key: string) {
    return new Promise<T | undefined>((res, rej) => {
      try {
        const transaction = this.db?.transaction(store, 'readonly');
        assert(transaction, 'Failed to create db transaction');
        const result = transaction?.objectStore(store).get(key);
        transaction.oncomplete = () => res(result.result);
        transaction.onerror = () => rej();
        transaction.onabort = () => rej();
      } catch(e) {
        rej();
      }
    });
  }

  async load<T>(store: string) {
    return new Promise<T[] | undefined>((res, rej) => {
      try {
        const transaction = this.db?.transaction(store, 'readonly');
        assert(transaction, 'Failed to create db transaction');
        const result = transaction?.objectStore(store).getAll();
        transaction.oncomplete = () => res(result.result);
        transaction.onerror = () => rej();
        transaction.onabort = () => rej();
      } catch (e) {
        rej();
      }
    });
  }

  async delete(store: string, key: string) {
    return new Promise<void>((res, rej) => {
      try {
        const transaction = this.db?.transaction(store, 'readwrite');
        assert(transaction, 'Failed to create db transaction');
        transaction?.objectStore(store).delete(key);
        transaction.commit();
        transaction.oncomplete = () => res();
        transaction.onerror = () => rej();
        transaction.onabort = () => rej();
      } catch (e) {
        rej();
      }
    });
  }

  async clear(store: string) {
    return new Promise<void>((res, rej) => {
      try {
        const transaction = this.db?.transaction(store, 'readwrite');
        assert(transaction, 'Failed to create db transaction');
        transaction?.objectStore(store).clear();
        transaction.commit();
        transaction.oncomplete = () => res();
        transaction.onerror = () => rej();
        transaction.onabort = () => rej();
      } catch (e) {
        rej();
      }
    });
  }
}
